import React, { useState, useEffect } from 'react';
import './PagesSidebar.css';
import { BiHome, BiPlus, BiDotsVerticalRounded, BiEdit, BiTrash, BiLayout, BiSearch, BiChevronDown, BiChevronRight, BiWindow, BiData, BiCart } from 'react-icons/bi';
import PageSettings from '../PageSettings/PageSettings';
import { createCheckoutElements } from '../Sections/Checkout1';

const PagesSidebar = ({ 
  elements,
  pages, 
  currentPage, 
  onPageChange, 
  onAddPage, 
  onDeletePage, 
  onRenamePage,
  onUpdatePage,
  isOpen,
  selectedContentItem,
  contentItems,
  setElements,
  setClassStylesRegistry,
  setGlobalClassNames,
  setSelectedElement,
  setNewlyAddedElement,
  showNotification,
  setPages,
  setCurrentPageId
}) => {
  const [editingPage, setEditingPage] = useState(null);
  const [newPageName, setNewPageName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showStaticPages, setShowStaticPages] = useState(true);
  const [showDynamicPages, setShowDynamicPages] = useState(true);

  const handleAddPage = () => {
    const pageName = `Page ${pages.length + 1}`;
    onAddPage(pageName);
  };

  const handleAddCheckoutPage = () => {
    const pageName = "Checkout";
    
    // First create the new page (it will be empty initially)
    const newPageId = onAddPage(pageName, []);

    // Get hierarchy name function for the checkout elements
    const getHierarchyName = (elementType) => {
      const elementCount = elements.filter(el => el.type === elementType).length + 1;
      return `${elementType.charAt(0).toUpperCase() + elementType.slice(1)} ${elementCount}`;
    };

    // Create checkout elements
    const { elements: checkoutElements } = createCheckoutElements(
      getHierarchyName,
      newPageId,
      setClassStylesRegistry,
      setGlobalClassNames
    );
    
    // Update the elements with the correct page ID
    const updatedCheckoutElements = checkoutElements.map(element => ({
      ...element,
      pageId: newPageId
    }));

    

    // Set the current page ID
    setCurrentPageId(newPageId);

    // Set the elements for the new page
    setElements(updatedCheckoutElements);
    
    // Set the first element as selected
    setSelectedElement(updatedCheckoutElements[0]);
    setNewlyAddedElement(updatedCheckoutElements[0]);
    
    // Show success notification
    showNotification(`Added Checkout page successfully`, "success");
  };

  const handleStartRename = (page) => {
    setEditingPage(page.id);
    setNewPageName(page.name);
  };

  const handleRename = (e) => {
    e.preventDefault();
    if (newPageName.trim()) {
      onRenamePage(editingPage, newPageName);
      setEditingPage(null);
      setNewPageName('');
    }
  };

  const handleDelete = (pageId) => {
    if (pages.length > 1 && window.confirm('Are you sure you want to delete this page?')) {
      onDeletePage(pageId);
    }
  };

  // Filter pages based on search term and type
  const staticPages = pages
    .filter(page => !page.isTemplate)
    .filter(page => page.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const dynamicPages = pages
    .filter(page => page.isTemplate)
    .filter(page => page.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const getCurrentPage = () => {
    return pages.find(page => page.id === currentPage);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="pages-sidebar">
        <div className="pages-sidebar-header">
          <h3>Pages</h3>
          <div className="pages-sidebar-actions">
            <button className="add-page-button" onClick={handleAddCheckoutPage}>
              <BiCart /> Add Checkout
            </button>
            <button className="add-page-button" onClick={handleAddPage}>
              <BiPlus /> New Page
            </button>
          </div>
        </div>

        <div className="pages-search">
          <div className="search-input-wrapper">
            <BiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search pages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="pages-sections">
          {/* Static Pages Section */}
          <div className="pages-section">
            <div 
              className="pages-category-header"
              onClick={() => setShowStaticPages(!showStaticPages)}
            >
              {showStaticPages ? <BiChevronDown /> : <BiChevronRight />}
            
              <span>Static Pages ({staticPages.length})</span>
            </div>
            
            {showStaticPages && (
              <div className="pages-category-content">
                {staticPages.map((page) => (
                  <div
                    key={page.id}
                    className={`page-item ${currentPage === page.id ? 'active' : ''}`}
                    onClick={() => onPageChange(page.id)}
                  >
                    <div className="page-item-content">
                      {page.isHome ? (
                        <BiHome className="page-icon" />
                      ) : (
                        <BiWindow className="page-icon" />
                      )}
                      {editingPage === page.id ? (
                        <form onSubmit={handleRename}>
                          <input
                            type="text"
                            value={newPageName}
                            onChange={(e) => setNewPageName(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                            onBlur={handleRename}
                          />
                        </form>
                      ) : (
                        <span className="page-name">{page.name}</span>
                      )}
                    </div>
                    
                    <div className="page-actions">
                      <button 
                        className="page-action-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartRename(page);
                        }}
                        title="Rename page"
                      >
                        <BiEdit />
                      </button>
                      {!page.isHome && (
                        <button 
                          className="page-action-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(page.id);
                          }}
                          title="Delete page"
                        >
                          <BiTrash />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dynamic Pages Section */}
          <div className="pages-section">
            <div 
              className="pages-category-header"
              onClick={() => setShowDynamicPages(!showDynamicPages)}
            >
              {showDynamicPages ? <BiChevronDown /> : <BiChevronRight />}
           
              <span>CMS Dynamic Pages ({dynamicPages.length})</span>
            </div>
            
            {showDynamicPages && (
              <div className="pages-category-content">
                {dynamicPages.map((page) => (
                  <div
                    key={page.id}
                    className={`page-item template ${currentPage === page.id ? 'active' : ''}`}
                    onClick={() => onPageChange(page.id)}
                  >
                    <div className="page-item-content">
                      <BiData className="page-icon" />
                      {editingPage === page.id ? (
                        <form onSubmit={handleRename}>
                          <input
                            type="text"
                            value={newPageName}
                            onChange={(e) => setNewPageName(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                            onBlur={handleRename}
                          />
                        </form>
                      ) : (
                        <span className="page-name">{page.name}</span>
                      )}
                    </div>
                    
                    <div className="page-actions">
                      <button 
                        className="page-action-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartRename(page);
                        }}
                        title="Rename page"
                      >
                        <BiEdit />
                      </button>
                      <button 
                        className="page-action-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(page.id);
                        }}
                        title="Delete page"
                      >
                        <BiTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {currentPage && (
        <PageSettings 
          page={getCurrentPage()}
          onUpdatePage={(settings) => {
            // Ensure customCode exists
            const updatedSettings = {
              ...settings,
              customCode: settings.customCode || { head: '', body: '' }
            };
            onUpdatePage(currentPage, updatedSettings);
          }}
          contentItems={contentItems}
          selectedContentItem={selectedContentItem}
        />
      )}
    </>
  );
};

export default PagesSidebar; 